import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import SidebarMenu from "../AdminPanel/SidebarMenu";
import TopBar from "../AdminPanel/TopBar";
import PaymentAccountPicker from "./PaymentAccountPicker";
import PersonaAutocomplete from "../../Empresa//NuevaFactura/InfoInvoice/PersonaAutocomplete";
import DocumentPaymentDetail from "./PaymentDocuments";
import { AppContext } from "../../../context/AppContext";

const upper = (v) => String(v || "").trim().toUpperCase();

const tdLabelSx = {
  width: 220,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 600,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1, verticalAlign: "middle" };
const inputSx = { width: "50%" };

const asOrigen = (v) => {
  const s = upper(v);
  if (s === "COMPRA") return "COMPRA";
  if (s === "VENTA") return "VENTA";
  return "";
};

const defaultTipoByOrigen = (o) => (o === "VENTA" ? "COBRO" : "PAGO");
const defaultFormaByTipo = (t) => (t === "COBRO" ? "CAJA" : "CAJA_CHICA");

const normPersona = (p) => {
  if (!p) return null;

  if (typeof p === "object") {
    const personaId = String(
      p?.persona_id ||
        p?.id ||
        p?.cliente_id ||
        p?.proveedor_id ||
        ""
    ).trim();

    const identificacion = String(
      p?.identificacion ||
        p?.cedula ||
        p?.ruc ||
        ""
    ).trim();

    const razonSocial = String(
      p?.razon_social ||
        p?.nombre_comercial ||
        p?.nombre ||
        ""
    ).trim();

    const tipoPersona = String(p?.tipo_persona || "").trim();

    return {
      id: personaId,
      persona_id: personaId,
      cliente_id: p?.cliente_id || "",
      proveedor_id: p?.proveedor_id || "",
      identificacion,
      razon_social: razonSocial,
      tipo_persona: tipoPersona,
    };
  }

  const s = String(p || "").trim();
  if (!s) return null;

  return {
    id: "",
    persona_id: "",
    cliente_id: "",
    proveedor_id: "",
    identificacion: "",
    razon_social: s,
    tipo_persona: "",
  };
};

const toMoney = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const sumDocs = (docs) => (Array.isArray(docs) ? docs.reduce((a, r) => a + toMoney(r?.pago), 0) : 0);

const docTypeLabel = (tipoDocumento) => {
  const value = upper(tipoDocumento);

  if (value === "01" || value === "FACTURA") return "FAC";
  if (value === "04" || value === "NOTA_CREDITO") return "NC";
  if (value === "05" || value === "NOTA_DEBITO") return "ND";
  if (value === "06" || value === "GUIA_REMISION") return "GR";
  if (value === "07" || value === "RETENCION") return "RET";

  return String(tipoDocumento || "").trim();
};

const Payment = () => {
  const { accessToken } = useContext(AppContext);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const origenQS = asOrigen(searchParams.get("origen"));
  const personaIdQS = String(searchParams.get("persona_id") || "").trim();
  const origenState = asOrigen(location.state?.origen);
  const origenInitial = origenState || origenQS;
  const personaFromState = location.state?.persona || null;

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [loadingInit, setLoadingInit] = useState(true);
  const initReqRef = useRef(0);

  const [data, setData] = useState({
    origen: origenInitial || "",
    tipo: defaultTipoByOrigen(origenInitial || "COMPRA"),
    forma: defaultFormaByTipo(defaultTipoByOrigen(origenInitial || "COMPRA")),
    fecha: "",
    persona: normPersona(personaFromState),
    personaId: personaIdQS || location.state?.persona_id || "",
    cuentaId: "",
    cuenta: "",
    banco: null,
    comprobante: "",
    efectivo: false,
    descripcion: "",
    documentos: [],
  });

  const [submitting, setSubmitting] = useState(false);

  const [resultModal, setResultModal] = useState({
    open: false,
    ok: false,
    title: "",
    message: "",
    pagoId: null,
    total: 0,
    payload: null,
  });

  const closeResultModal = () => setResultModal((p) => ({ ...p, open: false }));

  const authFetch = async (url, options = {}) => {
    const headers = {
      Accept: "application/json",
      ...(options.headers || {}),
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    return fetch(url, {
      ...options,
      credentials: "omit",
      headers,
    });
  };

  const tipoDocumentoPersona = useMemo(() => {
    return data.tipo === "PAGO" ? "PROVEEDOR" : "CLIENTE";
  }, [data.tipo]);

  const formas = useMemo(() => {
    if (data.tipo === "COBRO") {
      return [
        { v: "CAJA", l: "Caja" },
        { v: "TRANSFERENCIA", l: "Transferencia" },
        { v: "TARJETA_CREDITO", l: "Tarjeta de Crédito" },
        { v: "DINERO_ELECTRONICO", l: "Dinero Electrónico" },
      ];
    }

    return [
      { v: "CAJA_CHICA", l: "Caja Chica" },
      { v: "TRANSFERENCIA", l: "Transferencia" },
      { v: "TARJETA_CREDITO", l: "Tarjeta de Crédito" },
      { v: "DINERO_ELECTRONICO", l: "Dinero Electrónico" },
    ];
  }, [data.tipo]);

  useEffect(() => {
    const ok = formas.some((x) => x.v === data.forma);
    if (!ok) {
      const f = defaultFormaByTipo(data.tipo);
      setData((p) => ({
        ...p,
        forma: f,
        cuentaId: "",
        cuenta: "",
        banco: null,
        comprobante: "",
        efectivo: false,
      }));
    }
  }, [formas, data.forma, data.tipo]);

  useEffect(() => {
    let alive = true;
    const myReq = ++initReqRef.current;

    const fetchInit = async () => {
      setLoadingInit(true);

      const safeSetFromDoc = (doc) => {
        const docOrigen = upper(doc?.origen) === "COMPRA" ? "COMPRA" : "VENTA";
        const tipoDef = defaultTipoByOrigen(docOrigen);
        const formaDef = defaultFormaByTipo(tipoDef);
        const numero = String(doc?.numero_documento || "");
        const descReal = String(doc?.descripcion || doc?.descripcion_factura || doc?.referencia || "");
        const prefill = `${numero ? `Doc. ${numero}` : ""}${numero && descReal ? ", " : ""}${descReal || ""}`.trim();

        const personaObj =
          doc?.persona_detalle && typeof doc.persona_detalle === "object"
            ? doc.persona_detalle
            : doc?.persona && typeof doc.persona === "object"
            ? doc.persona
            : {
                id: doc?.persona_id || doc?.cliente_id || doc?.proveedor_id || "",
                persona_id: doc?.persona_id || doc?.cliente_id || doc?.proveedor_id || "",
                cliente_id: doc?.cliente_id || "",
                proveedor_id: doc?.proveedor_id || "",
                identificacion: doc?.comprador_identificacion || doc?.identificacion || "",
                razon_social: doc?.comprador_razon_social || doc?.razon_social || doc?.persona || "",
                tipo_persona: doc?.tipo_persona || "",
              };

        const normalizedPersona = normPersona(personaFromState) || normPersona(personaObj);
        const nextPersonaId =
          personaIdQS ||
          location.state?.persona_id ||
          normalizedPersona?.persona_id ||
          normalizedPersona?.id ||
          doc?.persona_id ||
          doc?.cliente_id ||
          doc?.proveedor_id ||
          "";

        if (!alive || initReqRef.current !== myReq) return;

        setData((p) => ({
          ...p,
          origen: docOrigen,
          tipo: tipoDef,
          forma: p.forma || formaDef,
          fecha: String(doc?.fecha_emision || ""),
          persona: p.persona || normalizedPersona,
          personaId: p.personaId || nextPersonaId || "",
          descripcion: prefill,
        }));
      };

      let origenFinal = origenInitial;
      let docFallback = null;

      if (!origenFinal) {
        try {
          const rDoc = await authFetch(`/facturas/documentos/${id}/`, { method: "GET" });
          const ctDoc = (rDoc.headers.get("content-type") || "").toLowerCase();
          if (!ctDoc.includes("application/json")) throw new Error("RESP_NOT_JSON");
          const jDoc = await rDoc.json();
          if (!rDoc.ok) throw new Error(jDoc?.error || "DOC_FAIL");
          docFallback = jDoc;
          origenFinal = upper(jDoc?.origen) === "COMPRA" ? "COMPRA" : "VENTA";
        } catch {
          origenFinal = "COMPRA";
        }
      }

      try {
        const r = await authFetch(`/cuentas/pagos/init/${origenFinal}/${id}/`, { method: "GET" });
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        if (!ct.includes("application/json")) throw new Error("RESP_NOT_JSON");
        const j = await r.json();
        if (!r.ok) throw new Error(j?.error || "INIT_FAIL");

        const tipoDef = upper(j?.tipo_default || defaultTipoByOrigen(origenFinal)) === "COBRO" ? "COBRO" : "PAGO";
        const formaDef = upper(j?.forma_default || defaultFormaByTipo(tipoDef));
        const personaObj = j?.persona_obj || {
          id: j?.persona_id || j?.cliente_id || j?.proveedor_id || "",
          persona_id: j?.persona_id || j?.cliente_id || j?.proveedor_id || "",
          cliente_id: j?.cliente_id || "",
          proveedor_id: j?.proveedor_id || "",
          identificacion: j?.identificacion || "",
          razon_social: j?.persona || "",
          tipo_persona: tipoDef === "COBRO" ? "CLIENTE" : "PROVEEDOR",
        };

        const normalizedPersona = normPersona(personaFromState) || normPersona(personaObj);
        const nextPersonaId =
          personaIdQS ||
          location.state?.persona_id ||
          normalizedPersona?.persona_id ||
          normalizedPersona?.id ||
          j?.persona_id ||
          j?.cliente_id ||
          j?.proveedor_id ||
          "";

        if (!alive || initReqRef.current !== myReq) return;

        setData((p) => ({
          ...p,
          origen: origenFinal,
          tipo: tipoDef,
          forma: p.forma || formaDef,
          fecha: String(j?.fecha_emision || ""),
          persona: p.persona || normalizedPersona || null,
          personaId: p.personaId || nextPersonaId || "",
          descripcion: String(j?.descripcion_prefill || p.descripcion || ""),
        }));
      } catch {
        if (docFallback) {
          safeSetFromDoc(docFallback);
        } else {
          try {
            const r2 = await authFetch(`/facturas/documentos/${id}/`, { method: "GET" });
            const ct2 = (r2.headers.get("content-type") || "").toLowerCase();
            if (!ct2.includes("application/json")) throw new Error("RESP_NOT_JSON");
            const j2 = await r2.json();
            if (!r2.ok) throw new Error(j2?.error || "DOC2_FAIL");
            safeSetFromDoc(j2);
          } catch {}
        }
      } finally {
        if (!alive || initReqRef.current !== myReq) return;
        setLoadingInit(false);
      }
    };

    if (id && accessToken) fetchInit();
    else setLoadingInit(false);

    return () => {
      alive = false;
    };
  }, [id, origenInitial, location.search, personaFromState, accessToken]);

  const formaU = upper(data.forma);
  const showEfectivo = formaU === "CAJA" || formaU === "CAJA_CHICA";
  const cuentaLabel = formaU === "TRANSFERENCIA" ? "Cuenta Bancaria:" : "Cuenta Pago / Cobro:";

  const onTipoChange = (e) => {
    const v = upper(e.target.value) === "COBRO" ? "COBRO" : "PAGO";
    const formaDef = defaultFormaByTipo(v);

    setData((p) => ({
      ...p,
      tipo: v,
      forma: formaDef,
      cuentaId: "",
      cuenta: "",
      banco: null,
      comprobante: "",
      efectivo: false,
      documentos: [],
    }));
  };

  const onFormaChange = (e) => {
    const v = upper(e.target.value);

    setData((p) => ({
      ...p,
      forma: v,
      cuentaId: "",
      cuenta: "",
      banco: null,
      efectivo: false,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((p) => ({ ...p, [name]: value }));
  };

  const handleSelectCuenta = ({ cuentaId, cuenta, banco }) => {
    setData((p) => ({
      ...p,
      cuentaId: String(cuentaId || ""),
      cuenta: String(cuenta || ""),
      banco: banco || null,
    }));
  };

  const handleDocsChange = (docs) => {
    setData((p) => ({ ...p, documentos: Array.isArray(docs) ? docs : [] }));
  };

  const handlePersonaChange = (p) => {
    const normalized = normPersona(p);
    const nextPersonaId = normalized?.persona_id || normalized?.id || "";

    setData((prev) => ({
      ...prev,
      persona: normalized,
      personaId: nextPersonaId,
      documentos: [],
    }));
  };

  const identificacion = String(data?.persona?.identificacion || "").trim();
  const personaId = String(data?.personaId || data?.persona?.persona_id || data?.persona?.id || "").trim();
  const total = useMemo(() => sumDocs(data.documentos), [data.documentos]);

  const buildDetalles = () => {
    const rows = Array.isArray(data.documentos) ? data.documentos : [];

    const detalles = rows
      .map((r) => ({
        doc_id: String(r?.doc_id || "").trim(),
        origen: upper(r?.origen),
        valor: toMoney(r?.pago),
        saldo: toMoney(r?.saldo),
        documento: String(r?.documento || "").trim(),
        tipo_documento: String(r?.tipo_documento || "").trim(),
      }))
      .filter((d) => d.doc_id && (d.origen === "VENTA" || d.origen === "COMPRA") && d.valor > 0);

    const invalid = detalles.some((d) => d.valor > d.saldo + 1e-9);
    if (invalid) return { ok: false, detalles: [], error: "Hay valores que exceden el saldo del documento." };
    if (!detalles.length) return { ok: false, detalles: [], error: "Debe agregar al menos un detalle con valor > 0." };

    return {
      ok: true,
      detalles: detalles.map(({ doc_id, origen, valor, documento, tipo_documento }) => ({
        doc_id,
        origen,
        valor,
        documento,
        tipo_documento,
      })),
      error: "",
    };
  };

  const validateBeforeSubmit = () => {
    if (!data.persona?.razon_social) return "Seleccione una persona.";
    if (!personaId && !identificacion) return "La persona no tiene ID ni identificación válida.";

    if (formaU === "TRANSFERENCIA") {
      const bancoId = String(data?.banco?.id || "");
      if (!bancoId) return "Seleccione un banco para transferencia.";
    } else {
      if (!data.cuentaId) return "Seleccione una cuenta.";
    }

    const det = buildDetalles();
    if (!det.ok) return det.error;
    if (total <= 0) return "El total debe ser mayor a 0.";
    return "";
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const msg = validateBeforeSubmit();
    if (msg) {
      setResultModal({
        open: true,
        ok: false,
        title: "No se puede registrar",
        message: msg,
        pagoId: null,
        total: 0,
        payload: null,
      });
      return;
    }

    const det = buildDetalles();

    const payload = {
      tipo: upper(data.tipo),
      origen: upper(data.origen),
      forma: formaU,
      persona_id: personaId || "",
      cliente_id: data.tipo === "COBRO" ? personaId || "" : "",
      proveedor_id: data.tipo === "PAGO" ? personaId || "" : "",
      persona: data.persona || undefined,
      comprobante: data.comprobante || "",
      efectivo: !!data.efectivo,
      descripcion: data.descripcion || "",
      total: Number(total.toFixed(2)),
      detalles: det.detalles.map((x) => ({ ...x, valor: Number(toMoney(x.valor).toFixed(2)) })),
    };

    if (formaU === "TRANSFERENCIA") {
      payload.banco_id = String(data?.banco?.id || "");
      payload.banco = data.banco || null;
    } else {
      payload.cuenta_id = String(data.cuentaId || "");
    }

    setSubmitting(true);

    try {
      const r = await authFetch("/cuentas/pagos/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const ct = (r.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) throw new Error("RESP_NOT_JSON");

      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || "NO_SE_PUDO_REGISTRAR");

      setResultModal({
        open: true,
        ok: true,
        title: "Pago registrado",
        message: "Se registró correctamente.",
        pagoId: j?.pago_id ?? null,
        total: j?.total ?? payload.total,
        payload,
      });

      setData((p) => ({
        ...p,
        cuentaId: "",
        cuenta: "",
        banco: null,
        comprobante: "",
        efectivo: false,
        documentos: [],
      }));
    } catch (e) {
      setResultModal({
        open: true,
        ok: false,
        title: "Error",
        message: e?.message === "RESP_NOT_JSON" ? "RESP_NOT_JSON" : e?.message || "No se pudo registrar.",
        pagoId: null,
        total: 0,
        payload: null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 800, color: "#1565C0", fontSize: "1.1rem" }}>
                  Registrar Cobro/Pago
                </Typography>
                <Typography sx={{ color: "#000", mt: 0.5 }}>Información de la Transacción</Typography>
              </Box>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loadingInit || submitting || !accessToken}
                sx={{
                  textTransform: "none",
                  bgcolor: "#1565C0",
                  "&:hover": { bgcolor: "#0D47A1" },
                  minWidth: 180,
                }}
              >
                {submitting ? "Registrando..." : "Registrar"}
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {loadingInit ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Cargando información…</Typography>
              </Box>
            ) : !accessToken ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography sx={{ color: "#d32f2f", fontWeight: 700 }}>
                  Sesión expirada. Inicia sesión nuevamente.
                </Typography>
              </Box>
            ) : (
              <>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={tdLabelSx}>Tipo de transacción:</TableCell>
                      <TableCell sx={tdFieldSx}>
                        <TextField select name="tipo" value={data.tipo} onChange={onTipoChange} sx={inputSx}>
                          <MenuItem value="COBRO">Cobro</MenuItem>
                          <MenuItem value="PAGO">Pago</MenuItem>
                        </TextField>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={tdLabelSx}>Forma de Pago:</TableCell>
                      <TableCell sx={tdFieldSx}>
                        <TextField select name="forma" value={data.forma} onChange={onFormaChange} sx={inputSx}>
                          {formas.map((f) => (
                            <MenuItem key={f.v} value={f.v}>
                              {f.l}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={tdLabelSx}>Fecha de emisión:</TableCell>
                      <TableCell sx={tdFieldSx}>
                        <TextField value={data.fecha} InputProps={{ readOnly: true }} sx={inputSx} />
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={tdLabelSx}>Persona:</TableCell>
                      <TableCell sx={tdFieldSx}>
                        <Box sx={{ width: "70%" }}>
                          <PersonaAutocomplete
                            value={data.persona}
                            onChange={handlePersonaChange}
                            tipoDocumento={tipoDocumentoPersona}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={tdLabelSx}>{cuentaLabel}</TableCell>
                      <TableCell sx={tdFieldSx}>
                        <PaymentAccountPicker
                          forma={data.forma}
                          value={data.cuenta}
                          onSelect={handleSelectCuenta}
                          label={cuentaLabel}
                          sx={inputSx}
                          disabled={false}
                          origen={data.origen}
                          tipo={data.tipo}
                        />
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={tdLabelSx}># de comprobante:</TableCell>
                      <TableCell sx={tdFieldSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <TextField
                            name="comprobante"
                            value={data.comprobante}
                            onChange={handleChange}
                            sx={{ width: "50%" }}
                          />
                          {showEfectivo && (
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Checkbox
                                  checked={data.efectivo}
                                  onChange={(e) => setData((p) => ({ ...p, efectivo: e.target.checked }))}
                                />
                              }
                              label="Efectivo"
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={tdLabelSx}>Descripción:</TableCell>
                      <TableCell sx={tdFieldSx}>
                        <TextField
                          multiline
                          rows={3}
                          name="descripcion"
                          value={data.descripcion}
                          onChange={handleChange}
                          sx={inputSx}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <Box sx={{ mt: 3 }}>
                  <DocumentPaymentDetail
                    key={`${personaId}-${identificacion}-${data.tipo}`}
                    value={data.documentos}
                    onChange={handleDocsChange}
                    identificacion={identificacion}
                    personaId={personaId}
                    tipo={data.tipo}
                  />
                </Box>

                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                  <Typography sx={{ fontWeight: 800, color: "#111" }}>
                    Total: ${Number(total || 0).toFixed(2)}
                  </Typography>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog open={resultModal.open} onClose={closeResultModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, color: resultModal.ok ? "#1565C0" : "#d32f2f" }}>
          {resultModal.title}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1, color: "#444" }}>{resultModal.message}</Typography>

          {resultModal.ok && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 800, color: "#111" }}>
                Pago:
              </Typography>
              <Typography sx={{ mt: 0.5, color: "#111" }}>
                Total: ${Number(resultModal.total || 0).toFixed(2)}
              </Typography>

              {resultModal.payload?.detalles?.length ? (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontWeight: 800, color: "#111", mb: 1 }}>Detalles</Typography>
                  <Box
                    sx={{
                      border: "1px solid #e6e6e6",
                      borderRadius: 1,
                      p: 1.5,
                      bgcolor: "#fafafa",
                    }}
                  >
                    {resultModal.payload.detalles.map((d, idx) => {
                      const tipoLabel = docTypeLabel(d.tipo_documento);
                      const numero = String(d.documento || d.doc_id || "").trim();
                      const label = tipoLabel ? `${tipoLabel} ${numero}` : numero;

                      return (
                        <Box
                          key={`${d.doc_id}-${idx}`}
                          sx={{ display: "flex", justifyContent: "space-between", py: 0.5, gap: 2 }}
                        >
                          <Typography sx={{ color: "#333" }}>{label}</Typography>
                          <Typography sx={{ fontWeight: 800, color: "#111" }}>
                            ${Number(d.valor || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeResultModal}
            variant="contained"
            sx={{
              bgcolor: resultModal.ok ? "#1565C0" : "#d32f2f",
              "&:hover": { bgcolor: resultModal.ok ? "#0D47A1" : "#b71c1c" },
              textTransform: "none",
            }}
          >
            Cerrar
          </Button>
          {resultModal.ok && (
            <Button
              onClick={() => {
                closeResultModal();
                navigate(-1);
              }}
              variant="outlined"
              sx={{ textTransform: "none" }}
            >
              Volver
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payment;